/**
 * Bài học theo khối — gói nội dung F-Future (`ffuture.lesson/1`).
 *
 * LessonDetail render component này khi `structured_payload.schema` bắt đầu bằng
 * `ffuture.lesson/` (bài N5 cũ vẫn đi `LanguageLessonView`). Video bài giảng vẫn ở
 * `VideoPlayer` phía trên; ở đây là thanh hiển thị + mục tiêu + các khối hoạt động.
 * Khối lạ → cảnh báo "chưa hỗ trợ", khối lỗi → cảnh báo riêng — không làm vỡ cả bài.
 */
import { Suspense, lazy, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { Alert, Card, Flex, Segmented, Space, Spin, Switch, Tag, Typography } from "antd";
import {
  AimOutlined,
  AppstoreOutlined,
  AudioOutlined,
  BookOutlined,
  CheckSquareOutlined,
  CommentOutlined,
  CustomerServiceOutlined,
  EditOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FontSizeOutlined,
  NumberOutlined,
  ReadOutlined,
} from "@ant-design/icons";

import { Doc, HinhAnh, TheTu, TracNghiem, VanBan } from "./blocks/core";
import {
  ChuHan,
  HoiThoai,
  NgheChonThanh,
  NgheSo,
  NoiCum,
  NoiThanh,
  SoDem,
} from "./blocks/language";
import { ViewContext, type ViewOptions } from "./shared";
import { BlockErrorBoundary } from "./ui";
import type { BlockProps, ContentBlock, ContentLessonPayload } from "./types";

const { Text } = Typography;

// Tập viết kéo theo thư viện hanzi-writer — chỉ tải khi bài có khối này.
const VietChu = lazy(() => import("./blocks/VietChu"));

interface BlockSpec {
  icon: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- registry nhiều kiểu khối
  component: ComponentType<BlockProps<any>>;
  extra?: (b: ContentBlock) => ReactNode;
}

/** Registry `kieu` → component. Thêm khối mới: viết component + đăng ký ở đây + importer. */
const REGISTRY: Record<string, BlockSpec> = {
  // Khối lõi
  van_ban: { icon: <FileTextOutlined />, component: VanBan },
  hinh_anh: { icon: <FileImageOutlined />, component: HinhAnh },
  doc: { icon: <ReadOutlined />, component: Doc },
  trac_nghiem: {
    icon: <CheckSquareOutlined />,
    component: TracNghiem,
    extra: () => <Tag>Không tính điểm</Tag>,
  },
  the_tu: { icon: <BookOutlined />, component: TheTu },
  // Khối ngôn ngữ
  hoi_thoai: { icon: <CommentOutlined />, component: HoiThoai },
  nghe_chon_thanh: { icon: <CustomerServiceOutlined />, component: NgheChonThanh },
  noi_thanh: { icon: <AudioOutlined />, component: NoiThanh },
  noi_cum: { icon: <AudioOutlined />, component: NoiCum },
  chu_han: { icon: <FontSizeOutlined />, component: ChuHan },
  viet_chu: { icon: <EditOutlined />, component: VietChu },
  so_dem: { icon: <NumberOutlined />, component: SoDem },
  nghe_so: { icon: <CustomerServiceOutlined />, component: NgheSo },
};

function blockTitle(b: ContentBlock): string {
  if (b.kieu === "hoi_thoai") return b.canh?.ten || "Hội thoại";
  return b.tieu_de || "";
}

function BlockCard({
  block,
  lessonSlug,
  language,
}: {
  block: ContentBlock;
  lessonSlug: string;
  language: string;
}) {
  const spec = REGISTRY[block.kieu];
  if (!spec) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 16 }}
        message={`Chưa hỗ trợ khối "${block.kieu}"`}
        description="Các phần khác của bài vẫn học được bình thường."
      />
    );
  }
  const Comp = spec.component;
  return (
    <Card
      size="small"
      style={{ marginTop: 16 }}
      title={
        <Space>
          {spec.icon ?? <AppstoreOutlined />}
          {blockTitle(block)}
        </Space>
      }
      extra={spec.extra?.(block)}
    >
      <BlockErrorBoundary kieu={block.kieu}>
        <Suspense fallback={<Spin />}>
          <Comp block={block} lessonSlug={lessonSlug} language={language} />
        </Suspense>
      </BlockErrorBoundary>
    </Card>
  );
}

type Reading = "py" | "hv" | "both";

export function BlockLessonView({
  payload,
  lessonSlug,
  language = "",
}: {
  payload: ContentLessonPayload;
  lessonSlug: string;
  language?: string;
}) {
  const lang = payload.language || language;
  const phienAm = payload.hien_thi?.phien_am ?? [];
  const hasPy = phienAm.includes("pinyin");
  const hasHv = phienAm.includes("han_viet");
  const [view, setView] = useState<ViewOptions>({ py: true, hv: true, phon: false });
  const reading: Reading = view.py && view.hv ? "both" : view.py ? "py" : "hv";
  const blocks = useMemo(() => payload.blocks ?? [], [payload.blocks]);

  return (
    <ViewContext.Provider value={view}>
      <Flex justify="space-between" align="center" gap={8} wrap>
        <Text type="secondary">
          {payload.duration_min ? `${payload.duration_min} phút · ` : ""}
          {blocks.length} hoạt động
        </Text>
        <Space wrap>
          {hasPy && hasHv && (
            <Segmented
              value={reading}
              onChange={(m) => setView({ ...view, py: m !== "hv", hv: m !== "py" })}
              options={[
                { label: "Pinyin", value: "py" },
                { label: "Hán Việt", value: "hv" },
                { label: "Cả hai", value: "both" },
              ]}
            />
          )}
          {payload.hien_thi?.phon_the && (
            <Space size={4}>
              <Switch
                size="small"
                checked={view.phon}
                onChange={(c) => setView({ ...view, phon: c })}
              />
              <Text>Phồn thể</Text>
            </Space>
          )}
        </Space>
      </Flex>
      {!!payload.muc_tieu?.length && (
        <Card
          size="small"
          style={{ marginTop: 16 }}
          title={
            <Space>
              <AimOutlined />
              Học xong bài này, bạn sẽ
            </Space>
          }
        >
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {payload.muc_tieu.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </Card>
      )}
      {blocks.map((b, i) => (
        <BlockCard key={b.id || i} block={b} lessonSlug={lessonSlug} language={lang} />
      ))}
    </ViewContext.Provider>
  );
}
